/**
 * Shared month-picker option builder used by month-based reports
 * (Doctor Business Summary, Product Usage Summary, etc.).
 *
 * Produces a list of months in descending order (current month first),
 * with labels like "April 26" and a stable `value` key for ngModel.
 */
export interface MonthOption {
  /** Display label, e.g. "April 26". */
  label: string;
  /** 1-12 */
  month: number;
  /** Full year, e.g. 2026. */
  year: number;
  /** Stable key used by ngModel: `${year}-${month}`. */
  value: string;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Build the last `count` months (default 60) descending from today.
 */
export function buildMonthOptions(count: number = 60, fromDate: Date = new Date()): MonthOption[] {
  const opts: MonthOption[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(fromDate.getFullYear(), fromDate.getMonth() - i, 1);
    const month = d.getMonth() + 1;
    const year = d.getFullYear();
    opts.push({
      label: `${MONTH_NAMES[d.getMonth()]} ${String(year).slice(-2)}`,
      month,
      year,
      value: `${year}-${month}`,
    });
  }
  return opts;
}

/**
 * Resolve a value-string back to its option, or undefined if not found.
 */
export function findMonthOption(opts: MonthOption[], value: string): MonthOption | undefined {
  return opts.find(o => o.value === value);
}

/**
 * Resolve an option from explicit month/year, or undefined if not in list.
 */
export function findMonthByMonthYear(opts: MonthOption[], month: number, year: number): MonthOption | undefined {
  return opts.find(o => o.month === month && o.year === year);
}
